import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    functional: false,
    analytics: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        // Invalid stored value, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem('cookie_consent', JSON.stringify(prefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true
    });
  };

  const acceptEssential = () => {
    saveConsent({
      essential: true,
      functional: false,
      analytics: false
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {/* Overlay for preferences modal */}
      {showPreferences && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setShowPreferences(false)}
        />
      )}

      {/* Main Banner */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-3 md:p-6"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Preferences Panel */}
          <AnimatePresence>
            {showPreferences && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-b border-gray-200"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
                    <button
                      onClick={() => setShowPreferences(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Essential Cookies */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">Essential Cookies</h4>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Required</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          These cookies are necessary for the website to function. They enable basic features like page navigation and access to secure areas.
                        </p>
                      </div>
                      <div className="ml-4">
                        <div className="w-12 h-7 bg-green-500 rounded-full flex items-center justify-end px-1">
                          <div className="w-5 h-5 bg-white rounded-full shadow" />
                        </div>
                      </div>
                    </div>

                    {/* Functional Cookies */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Functional Cookies</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          These cookies remember your preferences like language and theme settings to provide a personalized experience.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => setPreferences(p => ({ ...p, functional: !p.functional }))}
                          className={`w-12 h-7 rounded-full flex items-center transition-colors ${
                            preferences.functional ? 'bg-primary justify-end' : 'bg-gray-300 justify-start'
                          } px-1`}
                        >
                          <div className="w-5 h-5 bg-white rounded-full shadow" />
                        </button>
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          These cookies help us understand how visitors interact with our website by collecting anonymous information.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                          className={`w-12 h-7 rounded-full flex items-center transition-colors ${
                            preferences.analytics ? 'bg-primary justify-end' : 'bg-gray-300 justify-start'
                          } px-1`}
                        >
                          <div className="w-5 h-5 bg-white rounded-full shadow" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={savePreferences}
                      className="px-6 py-2 bg-primary text-white rounded-full font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Content */}
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Cookie className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-1">We value your privacy</h2>
                <p className="text-gray-600 text-xs md:text-sm hidden md:block">
                  We use cookies to enhance your browsing experience, provide personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies. 
                  <a href="/privacy" className="text-primary hover:underline ml-1">Read our Privacy Policy</a>
                </p>
                <p className="text-gray-600 text-xs md:hidden">
                  We use cookies to enhance your experience. 
                  <a href="/privacy" className="text-primary hover:underline ml-1">Read our Privacy Policy</a>
                </p>
              </div>
            </div>

            <div className="mt-4 md:mt-6 flex flex-wrap gap-2 md:gap-3 justify-end">
              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="px-3 md:px-5 py-2 md:py-2.5 border border-gray-300 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1 md:gap-2"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden md:inline">Manage Preferences</span>
                <span className="md:hidden">Manage</span>
              </button>
              <button
                onClick={acceptEssential}
                className="px-3 md:px-5 py-2 md:py-2.5 border border-gray-300 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Essential Only
              </button>
              <button
                onClick={acceptAll}
                className="px-4 md:px-5 py-2 md:py-2.5 bg-primary text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
